// src/pages/recruiter-management/components/AddRecruiterModal.jsx
import React, { useState, useEffect } from 'react';
import Icon from '../../../components/AppIcon';
import { recruiterAPI } from '../../../utils/api';
import Notifier from '../../../components/ui/Notifier';

const AddRecruiterModal = ({ isOpen, onClose, onSuccess }) => {
  const [formData, setFormData] = useState({
    name: '',
    address: '',
    country: '',
    representative_name: '',
    contact_no: '',
    fwcms_registered_name: '',
    point_of_entry: '',
    embassy: ''
  });

  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const [notification, setNotification] = useState({ show: false, type: '', message: '' });

  // Country options based on the second image requirements
  const countries = [
    'Bangladesh',
    'India', 
    'Indonesia',
    'Myanmar',
    'Nepal',
    'Pakistan',
    'Philippines',
    'Sri Lanka',
    'Thailand',
    'Vietnam'
  ];

  // Point of Entry options - only KLIA and KLIA2
  const pointOfEntryOptions = [
    'KLIA',
    'KLIA2'
  ];

  // Embassy options
  const embassyOptions = [
    'Embassy of Malaysia in Dhaka',
    'Embassy of Malaysia in New Delhi', 
    'Embassy of Malaysia in Jakarta',
    'Embassy of Malaysia in Yangon',
    'Embassy of Malaysia in Kathmandu',
    'Embassy of Malaysia in Islamabad',
    'Embassy of Malaysia in Manila',
    'Embassy of Malaysia in Colombo',
    'Embassy of Malaysia in Bangkok',
    'Embassy of Malaysia in Hanoi',
    'Other'
  ];

  useEffect(() => {
    if (!isOpen) {
      resetForm();
    }
  }, [isOpen]);

  const resetForm = () => {
    setFormData({
      name: '',
      address: '',
      country: '',
      representative_name: '',
      contact_no: '',
      fwcms_registered_name: '',
      point_of_entry: '',
      embassy: ''
    });
    setErrors({});
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formData.name.trim()) {
      newErrors.name = 'Recruiter name is required';
    } else if (formData.name.length < 2) {
      newErrors.name = 'Recruiter name must be at least 2 characters';
    }

    if (!formData.address.trim()) {
      newErrors.address = 'Address is required';
    }

    if (!formData.country) {
      newErrors.country = 'Country is required';
    }

    if (!formData.representative_name.trim()) {
      newErrors.representative_name = 'Representative name is required';
    }

    if (!formData.contact_no.trim()) {
      newErrors.contact_no = 'Contact number is required';
    } else if (!/^[\+]?[0-9\s\-\(\)]{7,20}$/.test(formData.contact_no)) {
      newErrors.contact_no = 'Please enter a valid contact number';
    }

    if (!formData.fwcms_registered_name.trim()) {
      newErrors.fwcms_registered_name = 'FWCMS registered name is required';
    }

    if (!formData.point_of_entry) {
      newErrors.point_of_entry = 'Point of entry is required';
    }

    // Embassy is optional - no validation required

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    
    // Clear error when user starts typing
    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }

    setLoading(true);
    try {
      const response = await recruiterAPI.createRecruiter(formData);
      
      if (response?.status === 'success' || response?.data) {
        const message = 'Recruiter created successfully!';
        setNotification({ show: true, type: 'success', message });
        
        // Call success handler and close modal after notification shows
        setTimeout(() => {
          onSuccess?.(response.data || response);
          onClose();
        }, 1500);
      } else {
        const errorMessage = response?.message || 'Failed to create recruiter. Please try again.';
        setNotification({ show: true, type: 'error', message: errorMessage });
      }
    } catch (error) {
      console.error('Error creating recruiter:', error);
      
      let errorMessage = 'Failed to create recruiter. Please try again.';
      
      if (error.response?.data?.message) {
        errorMessage = error.response.data.message;
      } else if (error.response?.data?.errors) {
        // Handle validation errors
        const validationErrors = error.response.data.errors;
        setErrors(validationErrors);
        const firstError = Object.values(validationErrors)[0];
        errorMessage = Array.isArray(firstError) ? firstError[0] : firstError;
      } else if (error.message) {
        errorMessage = error.message;
      }
      
      setNotification({ show: true, type: 'error', message: errorMessage });
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-start justify-center z-50 p-4 pt-20">
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-3xl max-h-[85vh] flex flex-col">
        {/* Fixed Header */}
        <div className="flex items-center justify-between p-6 border-b border-secondary-200">
          <div>
            <h2 className="text-xl font-semibold text-text-primary">Add New Recruiter</h2>
            <p className="text-sm text-secondary-600 mt-1">Create a new recruiter agency profile</p>
          </div>
          <button
            onClick={onClose}
            className="p-2 hover:bg-secondary-100 rounded-lg transition-colors duration-150"
            disabled={loading}
          >
            <Icon name="X" size={20} className="text-secondary-600" />
          </button>
        </div>

        {/* Scrollable Body */}
        <div className="flex-1 overflow-y-auto p-6">
          <form onSubmit={handleSubmit} className="space-y-6">
            {errors.general && (
              <div className="bg-error-50 border border-error-200 rounded-lg p-4">
                <div className="flex items-center">
                  <Icon name="AlertCircle" size={20} className="text-error-600 mr-2" />
                  <span className="text-sm text-error-700">{errors.general}</span>
                </div>
              </div>
            )}

            {/* Recruiter Basic Information */}
            <div className="space-y-4">
              <h3 className="text-lg font-medium text-text-primary border-b border-secondary-200 pb-2">
                Basic Information
              </h3>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {/* Recruiter Name */}
                <div className="md:col-span-2">
                  <label className="block text-sm font-medium text-secondary-700 mb-2">
                    Recruiter Name <span className="text-error-500">*</span>
                  </label>
                  <input
                    type="text"
                    name="name"
                    value={formData.name}
                    onChange={handleInputChange}
                    className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent ${
                      errors.name ? 'border-error-300' : 'border-secondary-300'
                    }`}
                    placeholder="Enter recruiter/agency name"
                    disabled={loading}
                  />
                  {errors.name && (
                    <p className="mt-1 text-sm text-error-600">{errors.name}</p>
                  )}
                </div>

                {/* FWCMS Registered Name */}
                <div className="md:col-span-2">
                  <label className="block text-sm font-medium text-secondary-700 mb-2">
                    FWCMS Registered Name <span className="text-error-500">*</span>
                  </label>
                  <input
                    type="text"
                    name="fwcms_registered_name"
                    value={formData.fwcms_registered_name}
                    onChange={handleInputChange}
                    className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent ${
                      errors.fwcms_registered_name ? 'border-error-300' : 'border-secondary-300'
                    }`}
                    placeholder="Enter FWCMS registered name"
                    disabled={loading}
                  />
                  {errors.fwcms_registered_name && (
                    <p className="mt-1 text-sm text-error-600">{errors.fwcms_registered_name}</p>
                  )}
                </div>

                {/* Address */}
                <div className="md:col-span-2">
                  <label className="block text-sm font-medium text-secondary-700 mb-2">
                    Address <span className="text-error-500">*</span>
                  </label>
                  <textarea
                    name="address"
                    value={formData.address}
                    onChange={handleInputChange}
                    rows={3}
                    className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent ${
                      errors.address ? 'border-error-300' : 'border-secondary-300'
                    }`}
                    placeholder="Enter complete address"
                    disabled={loading}
                  />
                  {errors.address && (
                    <p className="mt-1 text-sm text-error-600">{errors.address}</p>
                  )}
                </div>

                {/* Country */}
                <div>
                  <label className="block text-sm font-medium text-secondary-700 mb-2">
                    Source Country <span className="text-error-500">*</span>
                  </label>
                  <select
                    name="country"
                    value={formData.country}
                    onChange={handleInputChange}
                    className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent ${
                      errors.country ? 'border-error-300' : 'border-secondary-300'
                    }`}
                    disabled={loading}
                  >
                    <option value="">Select source country</option>
                    {countries.map(country => (
                      <option key={country} value={country}>{country}</option>
                    ))}
                  </select>
                  {errors.country && (
                    <p className="mt-1 text-sm text-error-600">{errors.country}</p>
                  )}
                </div>

                {/* Representative Name */}
                <div>
                  <label className="block text-sm font-medium text-secondary-700 mb-2">
                    Representative Name <span className="text-error-500">*</span>
                  </label>
                  <input
                    type="text"
                    name="representative_name"
                    value={formData.representative_name}
                    onChange={handleInputChange}
                    className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent ${
                      errors.representative_name ? 'border-error-300' : 'border-secondary-300'
                    }`}
                    placeholder="Enter representative name"
                    disabled={loading}
                  />
                  {errors.representative_name && (
                    <p className="mt-1 text-sm text-error-600">{errors.representative_name}</p>
                  )}
                </div>

                {/* Contact Number */}
                <div>
                  <label className="block text-sm font-medium text-secondary-700 mb-2">
                    Contact Number <span className="text-error-500">*</span>
                  </label>
                  <input
                    type="text"
                    name="contact_no"
                    value={formData.contact_no}
                    onChange={handleInputChange}
                    className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent ${
                      errors.contact_no ? 'border-error-300' : 'border-secondary-300'
                    }`}
                    placeholder="Enter contact number (e.g., +60123456789)"
                    disabled={loading}
                  />
                  {errors.contact_no && (
                    <p className="mt-1 text-sm text-error-600">{errors.contact_no}</p>
                  )}
                </div>

                {/* Point of Entry */}
                <div>
                  <label className="block text-sm font-medium text-secondary-700 mb-2">
                    Point of Entry (Dropdown) <span className="text-error-500">*</span>
                  </label>
                  <select
                    name="point_of_entry"
                    value={formData.point_of_entry}
                    onChange={handleInputChange}
                    className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent ${
                      errors.point_of_entry ? 'border-error-300' : 'border-secondary-300'
                    }`}
                    disabled={loading}
                  >
                    <option value="">Select point of entry</option>
                    {pointOfEntryOptions.map(option => (
                      <option key={option} value={option}>{option}</option>
                    ))}
                  </select>
                  {errors.point_of_entry && (
                    <p className="mt-1 text-sm text-error-600">{errors.point_of_entry}</p>
                  )}
                </div>

                {/* Embassy */}
                <div>
                  <label className="block text-sm font-medium text-secondary-700 mb-2">
                    Embassy at Source Country (FWCMS) <span className="text-secondary-500">(Optional)</span>
                  </label>
                  <select
                    name="embassy"
                    value={formData.embassy}
                    onChange={handleInputChange}
                    className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent ${
                      errors.embassy ? 'border-error-300' : 'border-secondary-300'
                    }`}
                    disabled={loading}
                  >
                    <option value="">Select embassy</option>
                    {embassyOptions.map(option => (
                      <option key={option} value={option}>{option}</option>
                    ))}
                  </select>
                  {errors.embassy && (
                    <p className="mt-1 text-sm text-error-600">{errors.embassy}</p>
                  )}
                </div>
              </div>
            </div>
          </form>
        </div>

        {/* Fixed Footer */}
        <div className="flex items-center justify-end space-x-3 p-6 border-t border-secondary-200">
          <button
            type="button"
            onClick={onClose}
            className="px-4 py-2 text-sm font-medium text-secondary-700 bg-white border border-secondary-300 rounded-lg hover:bg-secondary-50 focus:ring-2 focus:ring-primary-500 focus:border-transparent transition-colors duration-150"
            disabled={loading}
          >
            Cancel
          </button>
          <button
            onClick={handleSubmit}
            disabled={loading}
            className="px-4 py-2 text-sm font-medium text-white bg-primary-600 border border-transparent rounded-lg hover:bg-primary-700 focus:ring-2 focus:ring-primary-500 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed transition-colors duration-150"
          >
            {loading ? (
              <>
                <Icon name="Loader2" size={16} className="animate-spin mr-2" />
                Creating Recruiter...
              </>
            ) : (
              <>
                <Icon name="Plus" size={16} className="mr-2" />
                Create Recruiter
              </>
            )}
          </button>
        </div>

        {/* Notification */}
        <Notifier
          show={notification.show}
          type={notification.type}
          message={notification.message}
          onClose={() => setNotification({ show: false, type: '', message: '' })}
        />
      </div>
    </div>
  );
};

export default AddRecruiterModal;